import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { withSnackbar } from 'notistack';

import { setAlertActionCreator } from 'plugins/store/reducers/common.reducer';
import withEvents from 'eventBus/withEvents';
import eventBusTypes from 'eventBus/eventBusTypes';
import { createBroadcast } from 'services/marketing.service';

// @material
import withStyles from '@material-ui/core/styles/withStyles';

// core components
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import CardBody from 'components/Card/CardBody';
import LogoUpload from 'components/CustomUpload/LogoUpload';
import SnackMessage from 'components/Snackbar/SnackMessage';

// components
import MarketingCreateBroadcastUploadCsv from './MarketingCreateBroadcastUploadCsv';
import MarketingCreateBroadcastPreview from './MarketingCreateBroadcastPreview';

const styles = {
	wrapper: {
		'& label': {
			'@media (max-width: 1400px)': {
				fontSize: '13px',
			},
			'@media (max-width: 1279px)': {
				fontSize: '14px',
			},
		},
	},
	title: {
		color: '#3C4858',
		fontSize: '18px',
	},
};

const uploadImageInputParams = {
	tooltip: 'Click to upload an Image for the Broadcast',
	label: 'Upload Image',
	allowedFormats: '.jpg, .jpeg, .png',
	resolution: { width: 1024, height: 512 },
	maxSize: 1,
};

uploadImageInputParams.validationError = `Please, upload a file max ${uploadImageInputParams.maxSize} Mb,
	type: ${uploadImageInputParams.allowedFormats},
	${uploadImageInputParams.resolution.width}*${uploadImageInputParams.resolution.height}px`;

uploadImageInputParams.hint = `File size max ${uploadImageInputParams.maxSize} Mb,
	type: jpg/png,
	${uploadImageInputParams.resolution.width}*${uploadImageInputParams.resolution.height}px`;

class MarketingCreateBroadcast extends Component {
	state = {
		scvData: null,
		imageFile: null,
		imageBase64: '',
		createBroadcastCallback: null,
	};

	uploadCsv = React.createRef();

	componentDidMount() {
		const createBroadcastCallback = this.props.EventBus.addEventListener(
			eventBusTypes.CREATE_BROADCAST,
			this.createBroadcast,
		);
		this.setState({ createBroadcastCallback });
	}

	componentWillUnmount() {
		this.props.EventBus.removeEventListener(eventBusTypes.CREATE_BROADCAST, this.state.createBroadcastCallback);
	}

	resetForm = () => {
		this.setState({
			scvData: null,
			imageFile: null,
			imageBase64: '',
		});
		this.uploadCsv.current.handleClear();
	}

	sendData = async () => {
		this.props.setIsValidForCreate(false);
		try {
			const message = this.state.scvData?.message;
			const phoneNumbers = this.state.scvData?.phones;
			const marketingImage = this.state.imageFile;

			const result = await createBroadcast({
				message,
				phoneNumbers,
				marketingImage,
			});
			this.resetForm();
			if (result.success) {
				this.showMessage(
					[
						`Your Marketing Update has been sent to ${result.registeredCustomers} registered Customers from the 'Available countries' list!`,
						'It might take some time to deliver it'
					],
					'success'
				);
			} else {
				this.showMessage(
					[
						'The File you have uploaded doesn`t contain any  Phone numbers of the Customers registered in the \'Available countries\'!',
						'Your Marketing  Update is not sent'
					],
					'danger'
				);
			}
		} catch (error) {
			this.showMessage(error.resolvedErrorMessage);
		}
		this.checkIsValid();
	}

	createBroadcast = () => {
		if (this.state.scvData && this.state.imageFile) {
			this.sendData();
		} else {
			this.showMessage('To send Marketing Update you have to upload .csv file with the message and Image');
		}
	}

	showMessage = (message, color = 'danger') => {
		this.props.enqueueSnackbar(message, {
			content: (key, message) => <SnackMessage id={key} message={message} color={color} />,
		});
	};

	setCsvData = (scvData) => {
		this.setState({ scvData }, this.checkIsValid);
	};

	onImageChange = (imageFile, imageBase64) => {
		this.setState({ imageFile, imageBase64 }, this.checkIsValid);
	};

	checkIsValid = () => {
		const isValid = this.state.imageFile && this.state.scvData;
		this.props.setIsValidForCreate(Boolean(isValid));
		this.setIsUnsavedBroadcast();
	};

	setIsUnsavedBroadcast = () => {
		const isUnsavedBroadcast = this.state.scvData || this.state.imageFile;
		this.props.setIsUnsavedBroadcast(!!isUnsavedBroadcast);
	}

	render() {
		const { classes, setAlert } = this.props;

		return (
			<CardBody className={classes.wrapper}>
				<div className={classes.title}>Create new Broadcast</div>
				<GridContainer>
					<GridItem xs={12} sm={12} md={12} lg={6}>
						<MarketingCreateBroadcastUploadCsv
							setCsvData={this.setCsvData}
							ref={this.uploadCsv}
							setAlert={setAlert}
						/>
					</GridItem>
					<GridItem xs={12} sm={12} md={12} lg={6}>
						<LogoUpload
							{...uploadImageInputParams}
							onImageChange={this.onImageChange}
							isUnsaved={this.state.imageFile}
						/>
					</GridItem>
				</GridContainer>
				<MarketingCreateBroadcastPreview
					text={this.state.scvData?.message}
					image={this.state.imageBase64}
				/>
			</CardBody>
		);
	}
}

MarketingCreateBroadcast.propTypes = {
	classes: PropTypes.object.isRequired,
	setIsValidForCreate: PropTypes.func,
	setIsUnsavedBroadcast: PropTypes.func,
	setAlert: PropTypes.func,
	enqueueSnackbar: PropTypes.func,
	EventBus: PropTypes.object,
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
	setAlert: bindActionCreators(setAlertActionCreator, dispatch),
});

export default compose(
	withEvents,
	withSnackbar,
	withStyles(styles),
	connect(mapStateToProps, mapDispatchToProps),
)(MarketingCreateBroadcast)
